'use client';

import { useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import * as Dialog from '@/app_components/ui/Dialog';
import { cn } from '@/app_lib/utils/utils';

const modalStyles = tv({
  base: 'h-fit w-fit z-modal-content transition-transform',
  variants: {
    position: {
      center:
        'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=open]:fade-in data-[state=closed]:fade-out',
      left: 'fixed top-0 left-0 data-[state=closed]:slide-to-left data-[state=open]:slide-from-left',
      right: 'fixed top-0 right-0 data-[state=open]:slide-from-right data-[state=closed]:slide-to-right',
    },
  },
});

type ModalVariants = VariantProps<typeof modalStyles>;

interface BasicModalProps extends ModalVariants {
  className?: string;
  trigger: React.ReactNode;
  content: React.ReactNode;
  closeClassName?: string;
  title?: string;
  description?: string;
}

export const BasicModal = ({
  content,
  trigger,
  position = 'center',
  closeClassName,
  title = 'Modal',
  description = 'This is an opened modal',
}: BasicModalProps) => {
  const [open, setOpen] = useState(false);
  return (
    <Dialog.Root
      onOpenChange={() => {
        if (window.s2nApi) {
          window.s2nApi.float = open;
        }
        setOpen((prev) => !prev);
      }}
      open={open}
    >
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 bg-black/50 transition-opacity z-modal-backdrop',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          asChild
          className={cn('h-fit w-fit z-modal-content transition-transform', modalStyles({ position }))}
        >
          <div>
            <Dialog.Title className='sr-only'>{title}</Dialog.Title>
            <Dialog.Description className='sr-only'>{description}</Dialog.Description>
            {content}
            <Dialog.Close
              asChild
              className={cn('fixed top-6 right-6 w-fit h-fit', closeClassName)}
            >
              <div>
                <ActionableIcon>
                  <Icon icon='X' />
                </ActionableIcon>
              </div>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
